import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API } from 'aws-amplify';
import { getLandingPadsSuccess, getLandingPadsFailure } from '../actions/liveAircraftActions';
import { selectPads } from '../selectors/liveAircraftSelectors';
import { LandingPad } from '@m4a3/api-client';

export interface UsePadsWithRetryResult {
  pads: LandingPad[];
  isLoading: boolean;
  error: string | null;
}

export const usePadsWithRetry = () => {
  const dispatch = useDispatch();
  const pads = useSelector(selectPads);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const retryCountRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLandingPads = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const res: LandingPad[] = await API.get('api', `/addressfind/listLandingPads?includeInactivePads=true`, {});

      dispatch(getLandingPadsSuccess(res));
      retryCountRef.current = 0;
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch landing pads');
      dispatch(getLandingPadsFailure());
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const scheduleRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    const baseDelay = 5000; // 5 seconds
    const maxDelay = 180000; // 3 minutes
    const currentDelay = Math.min(baseDelay * Math.pow(2, retryCountRef.current), maxDelay);
    
    retryTimeoutRef.current = setTimeout(async () => {
      retryCountRef.current++;
      const success = await fetchLandingPads();
      
      if (!success) {
        scheduleRetry();
      }
    }, currentDelay);
  };

  useEffect(() => {
    // Only start fetching if we don't already have pads and we're not already loading
    if ((!pads || pads.length === 0) && !isLoading) {
      fetchLandingPads().then(success => {
        // If initial fetch failed, start retry cycle 
        if (!success) {
          scheduleRetry();
        }
      });
    }
  }, [pads, isLoading]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return { pads, isLoading, error } as UsePadsWithRetryResult;
}; 